import React, { useState } from 'react';
import { Tabs, Button } from 'antd';
import type { CpscItem } from '__generated__/graphql';
import { StyledTabContainer, StyledDeleteButton } from './styles';

export interface CpscComponentTabsProps {
  /** Items array from form (injected by FormItem) - Partial allows empty/incomplete items */
  value?: Partial<CpscItem>[];
  /** Callback to notify form of changes (injected by FormItem) */
  onChange?: (items: Partial<CpscItem>[]) => void;
  /** Label prefix for tabs (default: "Item") */
  tabLabel?: string;
  /** Minimum number of items (default: 1) */
  minItems?: number;
  /** Maximum number of items (optional) */
  maxItems?: number;
  /** Read-only mode */
  readOnly?: boolean;
  /** Disabled state */
  disabled?: boolean;
  /** Render function for tab content - receives item index */
  renderTabContent: (index: number) => React.ReactNode;
}

const CpscComponentTabs: React.FC<CpscComponentTabsProps> = ({
  value,
  onChange,
  tabLabel = 'Item',
  minItems = 1,
  maxItems,
  readOnly = false,
  disabled = false,
  renderTabContent,
}) => {
  // Use value from form or default to single empty item
  const items = value || [{}];

  // Internal state for active tab
  const [activeKey, setActiveKey] = useState('0');

  const canAdd = !maxItems || items.length < maxItems;
  const canRemove = items.length > minItems;
  const activeIndex = parseInt(activeKey, 10);

  // Add new item
  const handleAdd = () => {
    const newItems = [...items, {}];
    onChange?.(newItems);
    setActiveKey(String(newItems.length - 1));
  };

  // Remove item at index
  const handleRemove = (index: number) => {
    const newItems = items.filter((_, i) => i !== index);
    const updatedItems = newItems.length > 0 ? newItems : [{}];
    onChange?.(updatedItems);

    // Adjust active key if needed
    if (activeIndex >= updatedItems.length) {
      setActiveKey(String(Math.max(0, updatedItems.length - 1)));
    }
  };

  // Generate tab items with children (antd renders content automatically)
  const tabItems = items.map((_, index) => ({
    key: String(index),
    label: `${tabLabel} ${index + 1}`,
    children: renderTabContent(index),
  }));

  return (
    <StyledTabContainer>
      {/* Tabs with built-in content rendering */}
      <Tabs
        activeKey={activeKey}
        onChange={setActiveKey}
        type="line"
        items={tabItems}
        destroyInactiveTabPane={true}
        tabBarExtraContent={
          !readOnly &&
          canAdd && (
            <Button type="default" onClick={handleAdd} disabled={disabled}>
              Add {tabLabel}
            </Button>
          )
        }
      />

      {/* Delete Button */}
      {!readOnly && canRemove && (
        <StyledDeleteButton
          danger
          onClick={() => handleRemove(activeIndex)}
          disabled={disabled}
        >
          Delete {tabLabel}
        </StyledDeleteButton>
      )}
    </StyledTabContainer>
  );
};

export default CpscComponentTabs;
